import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { MaskLines } from "./Motion";
import { HERO_IMAGE } from "../data/content";
import { scrollToHash } from "../lib/smooth";

const ease = [0.22, 1, 0.36, 1];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-25%"]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), { stiffness: 80, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 80, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="top" ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-28 pb-16 sm:pt-36 lg:min-h-screen">
      <div className="mx-auto grid max-w-[1440px] items-end gap-12 px-5 sm:px-10 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="relative z-10 lg:col-span-7 lg:pb-16">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.2 }}
            className="mb-8 text-xs tracking-[0.28em] uppercase text-[#8A6A3E]">Personal stylist · New York City</motion.p>
          <h1 className="font-display text-[15vw] leading-[0.88] tracking-[-0.02em] text-[#1A1816] sm:text-[12vw] lg:text-[9.2vw] xl:text-[150px]" data-testid="hero-title">
            <MaskLines onLoad delay={0.3} lines={["Ulyana", <span key="s" className="italic text-[#8A6A3E] lg:pl-[1.2em]">Shmidt</span>]} />
          </h1>
          <div className="mt-10 max-w-xl">
            <MaskLines onLoad delay={0.75} className="font-display text-2xl leading-snug text-[#1A1816] sm:text-3xl"
              lines={["Clear, confident personal style", "for women who know who they are."]} />
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 1.2 }}
              className="mt-6 text-base leading-relaxed text-[#5E5953] sm:text-lg">
              Wardrobes built from scratch, closets edited into capsules, and couples styled in harmony — for women 40+ in New York and online.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease, delay: 1.35 }}
              className="mt-10 flex flex-wrap gap-4">
              <a href="#contact" onClick={(e) => { e.preventDefault(); scrollToHash("#contact"); }} className="btn-dark" data-testid="hero-cta-book">Book a consultation</a>
              <a href="#services" onClick={(e) => { e.preventDefault(); scrollToHash("#services"); }} className="btn-ghost" data-testid="hero-cta-services">
                See the looks <ArrowDownRight className="h-4 w-4" />
              </a>
            </motion.div>
          </div>
        </motion.div>

        <div className="relative lg:col-span-5" style={{ perspective: 1200 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative">
            <motion.div
              initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
              animate={{ clipPath: "inset(0% 0% 0% 0%)" }}
              transition={{ duration: 1.5, ease, delay: 0.15 }}
              className="relative aspect-[4/5] overflow-hidden bg-[#EDE8E1] lg:aspect-[3/4]"
            >
              <motion.img style={{ y: imgY, scale: 1.15 }} src={HERO_IMAGE} alt="Ulyana Shmidt on the steps of an Upper East Side townhouse, New York" className="h-full w-full object-cover" data-testid="hero-image" />
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 1, ease, delay: 1.5 }}
              className="absolute -bottom-6 -left-4 bg-[#1A1816] px-6 py-5 text-[#FAF8F5] sm:-left-10" style={{ transform: "translateZ(40px)" }}>
              <p className="text-[11px] tracking-[0.28em] uppercase text-[#CDBFA8]">Pricing</p>
              <p className="font-display text-2xl">On request</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
